import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Folder, FolderOpen, ChevronRight, ChevronLeft, Truck, Clock, MapPin, User, Briefcase, Settings, CalendarClock, X, ArrowRightCircle, StickyNote } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Link } from 'react-router-dom';
import QuickReserveDialog from '../QuickReserveDialog';
import { useTenantSettings } from '../../hooks/useTenantSettings';
import { formatTime, formatWeekdayShort } from '../../utils/dateFormatting';

const STATUS_COLORS = {
  confirmed: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',
  tbc: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const JobPill = ({ job, onJobClick }) => {
  const { time_format, date_format } = useTenantSettings();
  const startLabel = job.start_time ? formatTime(job.start_time, time_format) : '';
  const endLabel = (job.end_time || job.finish_time) ? formatTime(job.end_time || job.finish_time, time_format) : '';
  let endDateLabel = null;
  if (job.is_multi_day && job.end_date) {
    endDateLabel = formatWeekdayShort(job.end_date, date_format) || job.end_date;
  }
  return (
  <button
    type="button"
    onClick={() => onJobClick && job.job_id && onJobClick(job.job_id)}
    className="w-full text-left rounded-sm border border-zinc-800 bg-zinc-900/60 hover:bg-zinc-800 hover:border-zinc-700 transition-colors px-3 py-2 group/job"
    data-testid={`folder-job-${job.job_id}`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-semibold text-zinc-100 truncate">
        {job.job_number ? `#${job.job_number}` : 'Job'}{job.customer_name ? ` · ${job.customer_name}` : ''}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        {job.is_multi_day && job.day_index && job.total_days && (
          <Badge className="text-[10px] px-1.5 py-0 border bg-blue-500/20 text-blue-300 border-blue-500/30" data-testid={`folder-job-multiday-${job.job_id}`}>
            Day {job.day_index} of {job.total_days}
          </Badge>
        )}
        {job.status && (
          <Badge className={`text-[10px] px-1.5 py-0 border ${STATUS_COLORS[job.status] || STATUS_COLORS.pending}`}>
            {job.status.replace(/_/g, ' ')}
          </Badge>
        )}
      </div>
    </div>
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-zinc-400">
      {(startLabel || endLabel) && (
        <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{startLabel || '—'}{endLabel ? `–${endLabel}` : ''}</span>
      )}
      {endDateLabel && (
        <span className="inline-flex items-center gap-1" data-testid={`folder-job-enddate-${job.job_id}`}>
          <CalendarClock className="h-3 w-3" />until {endDateLabel}
        </span>
      )}
      {job.site_name && <span className="inline-flex items-center gap-1 truncate max-w-[180px]"><MapPin className="h-3 w-3" />{job.site_name}</span>}
      {job.operator_name && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{job.operator_name}</span>}
    </div>
    {job.notes && (
      <div className="mt-1 flex items-start gap-1 text-[11px] text-zinc-500 italic" data-testid={`folder-job-notes-${job.job_id}`}>
        <StickyNote className="h-3 w-3 mt-0.5 shrink-0" />
        <span className="line-clamp-2">{job.notes}</span>
      </div>
    )}
  </button>
  );
};

const AssetCard = ({ asset, onJobClick, quickReserve, holds = [], onReserve, onRelease, onConvert }) => {
  const idle = !asset.jobs || asset.jobs.length === 0;
  return (
    <div
      className={`card-industrial p-4 border border-zinc-800 bg-zinc-900 rounded-sm ${idle && holds.length === 0 ? 'opacity-60' : ''}`}
      data-testid={`folder-asset-${asset.equipment_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0">
          <p className="font-heading font-bold text-zinc-100 truncate tracking-tight">{asset.name}</p>
          <p className="text-[11px] uppercase tracking-[0.1em] font-bold text-zinc-500 truncate">{asset.equipment_type}</p>
        </div>
        {quickReserve ? (
          <button
            type="button"
            onClick={() => onReserve(asset)}
            className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-[#ffd108] hover:text-yellow-300 border border-[#ffd108]/40 hover:border-[#ffd108] rounded-sm px-2 py-1 transition-colors"
            data-testid={`reserve-asset-${asset.equipment_id}`}
          >
            <CalendarClock className="h-3.5 w-3.5" /> Reserve
          </button>
        ) : (
          <Truck className="h-4 w-4 text-zinc-600 shrink-0" />
        )}
      </div>

      {/* Soft holds */}
      {holds.map(h => (
        <div key={h.id} className="mb-1.5 rounded-sm border border-dashed border-[#ffd108]/50 bg-[#ffd108]/5 px-2.5 py-1.5" data-testid={`hold-${h.id}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#ffd108]">
              <CalendarClock className="h-3 w-3" /> Reserved
            </span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => onConvert(h)} title="Convert to job" className="text-zinc-400 hover:text-green-400 transition-colors" data-testid={`hold-convert-${h.id}`}>
                <ArrowRightCircle className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => onRelease(h)} title="Release hold" className="text-zinc-400 hover:text-red-400 transition-colors" data-testid={`hold-release-${h.id}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-300 mt-0.5 truncate">
            {h.customer_name || 'No customer'}{(h.start_time) ? ` · ${h.start_time}${h.end_time ? `–${h.end_time}` : ''}` : ''}
            {h.note ? ` · ${h.note}` : ''}
          </p>
        </div>
      ))}

      {idle ? (
        holds.length === 0 && <p className="text-xs text-zinc-500 italic">No jobs scheduled</p>
      ) : (
        <div className="space-y-1.5">
          {asset.jobs.map((j, i) => <JobPill key={`${asset.equipment_id}-${i}`} job={j} onJobClick={onJobClick} />)}
        </div>
      )}
    </div>
  );
};

const FolderCard = ({ folder, onOpen }) => {
  const previews = (folder.assets || []).slice(0, 3).map(a => a.name).filter(Boolean);
  return (
    <button
      type="button"
      onClick={() => onOpen(folder)}
      className="card-industrial relative flex flex-col justify-between p-4 bg-zinc-900/50 border border-zinc-800 hover:bg-zinc-800 hover:border-zinc-700 hover:-translate-y-0.5 transition-[transform,background-color,border-color] duration-200 cursor-pointer group rounded-sm text-left min-h-[132px] overflow-hidden"
      data-testid={`folder-card-${folder.id}`}
    >
      <span className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: folder.color || '#ffd108' }} />
      <div className="flex items-start justify-between">
        <Folder className="h-7 w-7 transition-transform duration-200 group-hover:scale-110" style={{ color: folder.color || '#ffd108' }} />
        <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:text-zinc-300 transition-colors" />
      </div>
      <div className="mt-3">
        <p className="font-heading font-bold text-lg text-zinc-100 tracking-tight truncate">{folder.name}</p>
        <p className="text-xs text-zinc-400 mt-0.5">
          {folder.asset_count} asset{folder.asset_count !== 1 ? 's' : ''} · {folder.job_count} job{folder.job_count !== 1 ? 's' : ''} today
        </p>
        {previews.length > 0 && (
          <p className="text-[11px] text-zinc-600 mt-1 truncate">{previews.join(' · ')}</p>
        )}
      </div>
    </button>
  );
};

export const ScheduleFolderView = ({ api, date, onJobClick, basePath = '', showConfigLink = true, quickReserve = false, onConverted }) => {
  const dateStr = typeof date === 'string' ? date : format(date || new Date(), 'yyyy-MM-dd');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ folders: [], ungrouped: null });
  const [openFolder, setOpenFolder] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [reserveDialog, setReserveDialog] = useState({ open: false, equipmentId: null });

  const fetchReservations = useCallback(async () => {
    if (!quickReserve) return;
    try {
      const res = await api.get(`${basePath}/reservations?start_date=${dateStr}&end_date=${dateStr}`);
      setReservations(res.data || []);
    } catch { /* non-fatal */ }
  }, [api, basePath, dateStr, quickReserve]);

  const fetchDay = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${basePath}/schedule-folders/day?date=${dateStr}`);
      setData(res.data);
      // keep drill-in state in sync if a folder is open
      setOpenFolder(prev => {
        if (!prev) return null;
        if (prev.id === '__ungrouped__') return res.data.ungrouped;
        return (res.data.folders || []).find(f => f.id === prev.id) || null;
      });
    } catch {
      toast.error('Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, [api, dateStr, basePath]);

  useEffect(() => { fetchDay(); }, [fetchDay]);
  useEffect(() => { fetchReservations(); }, [fetchReservations]);

  const holdsFor = (equipmentId) =>
    reservations.filter(r => r.equipment_id === equipmentId || (r.combo_equipment_ids || []).includes(equipmentId));

  const handleReserve = (asset) => setReserveDialog({ open: true, equipmentId: asset?.equipment_id || null });

  const handleRelease = async (hold) => {
    try {
      await api.delete(`${basePath}/reservations/${hold.id}`);
      toast.success('Hold released');
      fetchReservations();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to release hold'); }
  };

  const handleConvert = async (hold) => {
    try {
      const res = await api.post(`${basePath}/reservations/${hold.id}/convert`);
      toast.success('Converted to job');
      fetchReservations();
      if (onConverted && res.data?.job_id) onConverted(res.data.job_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Add a customer to the hold before converting'); }
  };

  const assetCardProps = { quickReserve, onReserve: handleReserve, onRelease: handleRelease, onConvert: handleConvert };

  const allFolders = [...(data.folders || [])];
  if (data.ungrouped && data.ungrouped.asset_count > 0) allFolders.push(data.ungrouped);

  if (loading) {
    return <div className="py-16 text-center text-zinc-500 animate-pulse" data-testid="folder-view-loading">Loading folders…</div>;
  }

  if (allFolders.length === 0) {
    return (
      <div className="py-16 flex flex-col items-center justify-center text-center border border-dashed border-zinc-800 rounded-sm" data-testid="folder-view-empty">
        <FolderOpen className="h-10 w-10 text-zinc-700 mb-3" />
        <p className="text-zinc-300 font-medium">No folders configured</p>
        <p className="text-sm text-zinc-500 mt-1 mb-4">Set up your schedule folders and assign asset types.</p>
        {showConfigLink && (
          <Link to="/data-management?tab=schedule-folders" className="inline-flex items-center gap-2 text-sm text-[#ffd108] hover:underline" data-testid="folder-view-configure-link">
            <Settings className="h-4 w-4" /> Configure in Data Management
          </Link>
        )}
      </div>
    );
  }

  // Drill-in
  if (openFolder) {
    const assets = openFolder.assets || [];
    return (
      <div data-testid="folder-drill-in">
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-4">
          <button type="button" onClick={() => setOpenFolder(null)} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="folder-breadcrumb-back">
            <ChevronLeft className="h-4 w-4" /> Folders
          </button>
          <ChevronRight className="h-3 w-3 text-zinc-600" />
          <span className="text-white font-medium inline-flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: openFolder.color || '#ffd108' }} />
            {openFolder.name}
          </span>
        </div>
        {assets.length === 0 ? (
          <div className="py-12 text-center text-zinc-500 border border-dashed border-zinc-800 rounded-sm" data-testid="folder-empty-assets">
            <Briefcase className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
            No assets found for this folder type.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-in fade-in duration-200">
            {assets.map(a => <AssetCard key={a.equipment_id} asset={a} onJobClick={onJobClick} holds={holdsFor(a.equipment_id)} {...assetCardProps} />)}
          </div>
        )}
        <QuickReserveDialog
          open={reserveDialog.open}
          onOpenChange={(o) => setReserveDialog(s => ({ ...s, open: o }))}
          api={api}
          basePath={basePath}
          presetEquipmentId={reserveDialog.equipmentId}
          presetDate={dateStr}
          onCreated={fetchReservations}
        />
      </div>
    );
  }

  // Folder grid
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 animate-in fade-in duration-200" data-testid="folder-grid">
      {allFolders.map(f => <FolderCard key={f.id} folder={f} onOpen={setOpenFolder} />)}
    </div>
  );
};

export default ScheduleFolderView;
